/**
 * Groupes de workflows N8N — chantier Booster, lot T1 (plan validé :
 * `docs/plans/booster-t1-groupes-workflows-n8n.md`).
 *
 * Une ligne de `n8n_workflows` est un GROUPE de workflows (scénario, simulation, coach, feedback…).
 * Un simulateur choisit le sien (`simulators.n8nWorkflowId`) ; sans réglage, il suit le groupe par
 * défaut (`isDefault`, au plus une ligne, garanti par la base). Coach Prepa et S'exercer lisent
 * toujours le groupe par défaut.
 *
 * Fichier PARTAGÉ client + serveur : aucune dépendance (ni base, ni i18next).
 */

export interface LigneGroupeWorkflows {
  id: string;
  name?: string | null;
  isDefault?: boolean | null;
}

export type ChoixGroupe<T extends LigneGroupeWorkflows> =
  | { ligne: T; avertissement?: undefined }
  | { ligne: T; avertissement: "aucune_ligne_par_defaut" }
  | { ligne: null; avertissement?: "groupe_introuvable" };

/**
 * Ligne de workflows à appeler pour un simulateur.
 *
 * - Sans groupe (`idGroupe` vide) : la ligne par défaut. Si aucune ligne n'est marquée (base pas
 *   encore migrée), la première, avec un avertissement à journaliser.
 * - Avec un groupe : SA ligne. Introuvable ⇒ `null`, JAMAIS de repli sur la ligne par défaut :
 *   un simulateur Booster ne doit pas partir en silence vers les workflows de vente. L'appelant
 *   répond alors « Configuration N8N manquante ».
 */
export function choisirGroupeWorkflows<T extends LigneGroupeWorkflows>(lignes: T[], idGroupe?: string | null): ChoixGroupe<T> {
  if (idGroupe) {
    const ligne = lignes.find((l) => l.id === idGroupe);
    return ligne ? { ligne } : { ligne: null, avertissement: "groupe_introuvable" };
  }
  const parDefaut = lignes.find((l) => l.isDefault === true);
  if (parDefaut) return { ligne: parDefaut };
  const premiere = lignes[0];
  return premiere ? { ligne: premiere, avertissement: "aucune_ligne_par_defaut" } : { ligne: null };
}

/**
 * Réglages réservés à l'ADM dans le visualiseur admin (décision de Valérie du 16/10/2026) :
 * aussi dangereux qu'un code modifiable.
 * - le groupe d'un simulateur : un rattachement erroné le couperait de ses workflows ;
 * - le statut « groupe par défaut » : le déplacer enverrait d'un clic tous les simulateurs sans
 *   groupe (et Coach Prepa, S'exercer) vers d'autres workflows.
 * - « réservé aux internes » d'un simulateur (19/10/2026) : le décocher l'exposerait aux clients.
 * Le COO garde ses droits sur le reste des deux tables.
 */
const CHAMPS_RESERVES_ADM: Record<string, readonly string[]> = {
  // `internalOnly` (19/10/2026) : le retirer exposerait aux clients un simulateur en test.
  simulators: ["n8nWorkflowId", "internalOnly"],
  n8n_workflows: ["isDefault"],
};

/** Champs d'une table que ce profil ne peut pas modifier (vide pour l'ADM). */
export function champsReservesAdm(profileType: string, tableName: string): readonly string[] {
  if (profileType === "ADM") return [];
  return CHAMPS_RESERVES_ADM[tableName] ?? [];
}

/** Simulateurs qui changeraient de workflows si le groupe par défaut changeait : ceux sans groupe. */
export function simulateursSansGroupe(simulateurs: { n8nWorkflowId?: string | null }[]): number {
  return simulateurs.filter((s) => !s.n8nWorkflowId).length;
}

/**
 * Clé `simulatorId` à ajouter aux payloads N8N (condition c, plan
 * `docs/plans/booster-creation-simulatorid.md`) : seulement pour un simulateur rattaché à un groupe
 * qui n'est PAS le groupe par défaut. Les workflows existants reçoivent exactement le même payload
 * qu'avant (la clé est absente, pas vide) ; ceux des managers routent sur cet identifiant.
 */
export function champSimulatorIdPourN8n(
  simulateur: { id?: string | null; n8nWorkflowId?: string | null } | null | undefined,
  groupe: { isDefault?: boolean | null } | null | undefined,
): { simulatorId?: string } {
  if (!simulateur?.id || !simulateur.n8nWorkflowId || !groupe || groupe.isDefault === true) return {};
  return { simulatorId: simulateur.id };
}

/**
 * T6 (plan `docs/plans/booster-t6-exclusion-analyse-en-direct.md`) : un simulateur rattaché à un
 * groupe AUTRE que le groupe par défaut est exclu de l'analyse en direct — le contrôleur ne connaît
 * que les simulateurs de vente. Sans groupe (tous les simulateurs existants) : jamais exclu.
 * Lue par `POST /api/sideband` et, via `liveFeedback` de `GET /api/agents`, par l'écran.
 */
export function horsGroupeParDefaut(
  simulateur: { n8nWorkflowId?: string | null } | null | undefined,
  idGroupeParDefaut: string | null | undefined,
): boolean {
  return !!simulateur?.n8nWorkflowId && simulateur.n8nWorkflowId !== idGroupeParDefaut;
}
